import type { LexData } from "./types";
import { buildParticipationLeaderboard } from "./participation";

// View-model for the Admin → Mission KPIs dashboard. Everything here is derived
// from real network data; there is no fabricated period-over-period delta.

const DAY = 24 * 60 * 60 * 1000;
const PERIOD_MS = 30 * DAY;
const QUARTER_MS = 90 * DAY;

const COUNTRY_CODES: Record<string, string> = {
  Belgium: "BE", Germany: "DE", Switzerland: "CH", Sweden: "SE", Luxembourg: "LU", France: "FR",
  Netherlands: "NL", Italy: "IT", Spain: "ES", Poland: "PL", Austria: "AT", Portugal: "PT",
  Ireland: "IE", Denmark: "DK", Finland: "FI", Norway: "NO", "Czech Republic": "CZ", Greece: "GR",
};

export function countryCode(jurisdiction: string) {
  return COUNTRY_CODES[jurisdiction] ?? jurisdiction.slice(0, 2).toUpperCase();
}

export type DashboardKpi = { key: string; label: string; value: string; caption: string; chip?: string; delta?: { value: number; suffix: string; direction: "up" | "down" }; series: number[] };
export type ParticipationFirm = { firmId: string; name: string; code: string; pct: number; suspended: boolean };
export type NewsItem = { id: string; category: string; iconKey: string; text: string; timeLabel: string };
export type ReferralRow = { id: string; fromCode: string; fromName: string; toCode: string; toName: string; area: string; status: string; tone: "ok" | "warn" | "danger" | "neutral"; timeLabel: string };
export type TopMember = { rank: number; name: string; initials: string; firmName: string; firmCode: string; avatarUrl: string | null; votes: number; posts: number; referrals: number; score: number; pct: number };

export interface AdminDashboard {
  kpis: DashboardKpi[];
  participation: { ratePct: number; activeReps: number; totalReps: number; firms: ParticipationFirm[] };
  news: NewsItem[];
  latestVote: { title: string; meta: string; forCount: number; againstCount: number; abstainCount: number; notVoted: number; quorumReached: number; quorumTotal: number; closesLabel: string; proposalId: string } | null;
  topFirm: { name: string; code: string; jurisdiction: string; category: string; score: number; projectsLed: number; votesCast: number; referralsMade: number } | null;
  expertise: { introductions: number; engaged: number; mattersOpened: number; topArea: string; recent: ReferralRow[] };
  topMembers: TopMember[];
}

function initials(name: string) {
  return name.split(" ").map((part) => part[0]).filter(Boolean).join("").slice(0, 2).toUpperCase();
}

function relTime(timestamp: number, now: number) {
  const minutes = (now - timestamp) / 60000;
  if (minutes < 1) return "just now";
  const hours = minutes / 60;
  if (hours < 1) return `${Math.round(minutes)}m ago`;
  const days = hours / 24;
  if (days < 1) return `${Math.round(hours)}h ago`;
  if (days < 7) return `${Math.round(days)}d ago`;
  return `${Math.round(days / 7)}w ago`;
}

function dailySeries(timestamps: number[], now: number, days = 14) {
  const buckets = new Array(days).fill(0) as number[];
  for (const ts of timestamps) {
    const dayIndex = Math.floor((now - ts) / DAY);
    if (dayIndex >= 0 && dayIndex < days) buckets[days - 1 - dayIndex] += 1;
  }
  return buckets;
}

function newsMeta(type: string): { category: string; iconKey: string } {
  const t = type.toLowerCase();
  if (t.includes("vote") && (t.includes("open") || t.includes("propos"))) return { category: "Vote opened", iconKey: "vote" };
  if (t.includes("vote") || t.includes("decid") || t.includes("certif")) return { category: "Vote closed", iconKey: "voteClosed" };
  if (t.includes("mandate")) return { category: "Mandate", iconKey: "mandate" };
  if (t.includes("outreach")) return { category: "Outreach", iconKey: "legislative" };
  if (t.includes("endors")) return { category: "Position co-signed", iconKey: "legislative" };
  if (t.includes("lobby") || t.includes("legislat") || t.includes("position")) return { category: "Legislative file", iconKey: "legislative" };
  if (t.includes("referral") || t.includes("cooperation") || t.includes("expertise") || t.includes("introduction")) return { category: "Expertise request", iconKey: "exchange" };
  if (t.includes("public")) return { category: "Public site", iconKey: "public" };
  if (t.includes("project") || t.includes("milestone")) return { category: "Project milestone", iconKey: "project" };
  if (t.includes("member") || t.includes("firm") || t.includes("admission") || t.includes("joined")) return { category: "Member added", iconKey: "member" };
  return { category: type.charAt(0).toUpperCase() + type.slice(1), iconKey: "update" };
}

const FOR_VOTES = new Set(["strong_support", "support", "support_with_reservations", "yes"]);
const AGAINST_VOTES = new Set(["oppose", "object_unless_amended", "no"]);

export function buildAdminDashboard(data: LexData, now = Date.now()): AdminDashboard {
  const activeFirms = data.firms.filter((firm) => firm.status === "active");
  const fullMembers = activeFirms.filter((firm) => firm.membershipCategory === "Full Member");
  const reps = data.users.filter((user) => user.active && user.firmId);
  const totalReps = reps.length;
  const activeReps = reps.filter((user) => user.lastSeenAt && now - user.lastSeenAt <= PERIOD_MS).length;
  const notSignedIn = reps.filter((user) => !user.lastSeenAt).length;

  // Who participated (voted / posted / exchanged) in the last 30 days.
  const recent = (timestamp: number) => now - timestamp <= PERIOD_MS;
  const participated = new Set<string>();
  for (const vote of data.votes) if (recent(vote.createdAt) && vote.userId) participated.add(vote.userId);
  for (const discussion of data.discussions) if (recent(discussion.createdAt) && discussion.createdByUserId) participated.add(discussion.createdByUserId);
  for (const contribution of data.contributions) if (recent(contribution.createdAt) && contribution.createdByUserId) participated.add(contribution.createdByUserId);
  for (const request of data.cooperationRequests) if (recent(request.createdAt)) { participated.add(request.requesterUserId); if (request.targetUserId) participated.add(request.targetUserId); }
  const participatingReps = reps.filter((user) => participated.has(user.id)).length;
  const ratePct = totalReps > 0 ? Math.round((participatingReps / totalReps) * 100) : 0;

  const openProposals = data.proposals.filter((proposal) => proposal.votingOpensAt <= now && proposal.votingClosesAt >= now);
  const closingSoon = openProposals.filter((proposal) => proposal.votingClosesAt - now <= 2 * DAY).length;
  const exchangesQtd = data.cooperationRequests.filter((request) => now - request.createdAt <= QUARTER_MS).length;
  const mattersOpened = data.cooperationOutcomes.filter((outcome) => ["cooperation_started", "completed"].includes(outcome.outcomeType)).length;

  // Real period-over-period delta vs the oldest snapshot in the trailing window.
  const snapshots = (data.dashboardSnapshots ?? []).slice().sort((a, b) => a.capturedAt - b.capturedAt);
  const priorSnapshot = snapshots.find((snapshot) => now - snapshot.capturedAt >= 21 * DAY) ?? (snapshots.length > 1 ? snapshots[0] : undefined);
  const makeDelta = (current: number, previous: number | undefined, suffix: string): DashboardKpi["delta"] => {
    if (previous === undefined) return undefined;
    const diff = current - previous;
    if (diff === 0) return undefined;
    return { value: Math.abs(diff), suffix, direction: diff > 0 ? "up" : "down" };
  };

  const kpis: DashboardKpi[] = [
    {
      key: "participation",
      label: "Participation rate",
      value: `${ratePct}%`,
      caption: `${participatingReps} of ${totalReps} reps active this quarter`,
      delta: makeDelta(ratePct, priorSnapshot?.participationRate, " pts"),
      series: dailySeries([...data.votes, ...data.contributions].map((item) => item.createdAt), now),
    },
    {
      key: "activeReps",
      label: "Active reps · 30d",
      value: `${activeReps}/${totalReps}`,
      caption: notSignedIn > 0 ? `${notSignedIn} rep${notSignedIn === 1 ? "" : "s"} not yet signed in` : "All reps have signed in",
      delta: makeDelta(activeReps, priorSnapshot?.activeReps, ""),
      series: dailySeries(reps.map((user) => user.lastSeenAt ?? 0).filter(Boolean), now),
    },
    {
      key: "openVotes",
      label: "Open votes",
      value: String(openProposals.length),
      caption: openProposals.length > 0 ? "Joint decisions in progress" : "No open votes",
      chip: closingSoon > 0 ? `${closingSoon} closing` : undefined,
      series: dailySeries(data.votes.map((vote) => vote.createdAt), now),
    },
    {
      key: "exchanges",
      label: "Expertise exchanges · QTD",
      value: String(exchangesQtd),
      caption: `${mattersOpened} matter${mattersOpened === 1 ? "" : "s"} opened across firms`,
      delta: makeDelta(exchangesQtd, priorSnapshot?.exchangesQtd, ""),
      series: dailySeries(data.cooperationRequests.map((request) => request.createdAt), now),
    },
  ];

  // Per-firm contribution index (relative), drives the participation bars + scores.
  const leaderboard = buildParticipationLeaderboard(data);
  const referralsByFirm = (firmId: string) => data.cooperationRequests.filter((request) => request.requesterFirmId === firmId || request.targetFirmId === firmId).length;
  const firmContribution = (firmId: string) => {
    const fc = leaderboard.find((entry) => entry.firmId === firmId);
    const base = fc ? fc.votesCast + fc.comments + fc.amendmentsSponsored + fc.legislativeContributions : 0;
    return base + referralsByFirm(firmId);
  };
  const contributionByFirm = new Map(activeFirms.concat(data.firms.filter((firm) => firm.status === "suspended")).map((firm) => [firm.id, firmContribution(firm.id)]));
  const maxContribution = Math.max(1, ...contributionByFirm.values());
  const participationFirms: ParticipationFirm[] = data.firms
    .filter((firm) => firm.membershipCategory !== "Observer")
    .map((firm) => ({ firmId: firm.id, name: firm.name, code: countryCode(firm.jurisdiction), pct: Math.round(((contributionByFirm.get(firm.id) ?? 0) / maxContribution) * 100), suspended: firm.status === "suspended" }))
    .sort((a, b) => Number(a.suspended) - Number(b.suspended) || b.pct - a.pct)
    .slice(0, 8);

  const firmName = (id: string | null | undefined) => data.firms.find((firm) => firm.id === id)?.name ?? "A firm";
  const firmCode = (id: string | null | undefined) => { const firm = data.firms.find((item) => item.id === id); return firm ? countryCode(firm.jurisdiction) : "··"; };
  const areaName = (id: string | undefined) => data.practiceAreas.find((area) => area.id === id)?.name ?? "General";

  const news: NewsItem[] = (data.activityEvents ?? []).slice(0, 7).map((event) => {
    const meta = newsMeta(event.type);
    return { id: event.id, category: meta.category.toUpperCase(), iconKey: meta.iconKey, text: event.summary || event.title, timeLabel: relTime(event.createdAt, now) };
  });

  // Latest vote — prefer an open proposal, else the most recent.
  const sortedProposals = data.proposals.slice().sort((a, b) => b.votingOpensAt - a.votingOpensAt);
  const voteProposal = openProposals.slice().sort((a, b) => b.votingOpensAt - a.votingOpensAt)[0] ?? sortedProposals[0] ?? null;
  let latestVote: AdminDashboard["latestVote"] = null;
  if (voteProposal) {
    const proposalVotes = data.votes.filter((vote) => vote.proposalId === voteProposal.id && !vote.supersededByVoteId);
    const forCount = proposalVotes.filter((vote) => FOR_VOTES.has(vote.voteValue)).length;
    const againstCount = proposalVotes.filter((vote) => AGAINST_VOTES.has(vote.voteValue)).length;
    const abstainCount = proposalVotes.length - forCount - againstCount;
    const firmsVoted = new Set(proposalVotes.map((vote) => vote.firmId)).size;
    const quorumTotal = fullMembers.length;
    const title = data.discussions.find((discussion) => discussion.id === voteProposal.discussionId)?.title ?? "Governance proposal";
    const openedDays = Math.max(0, Math.round((now - voteProposal.votingOpensAt) / DAY));
    const closesDays = Math.round((voteProposal.votingClosesAt - now) / DAY);
    latestVote = {
      proposalId: voteProposal.id,
      title,
      meta: `${voteProposal.category} · ${voteProposal.thresholdType === "simple" ? "ordinary" : "binding"} · Opened ${openedDays}d ago`,
      forCount, againstCount, abstainCount,
      notVoted: Math.max(0, quorumTotal - firmsVoted),
      quorumReached: firmsVoted,
      quorumTotal,
      closesLabel: closesDays > 0 ? `Closes in ${closesDays} days` : voteProposal.votingClosesAt < now ? "Closed" : "Closes today",
    };
  }

  const top = leaderboard[0];
  const topFirmRecord = top ? data.firms.find((firm) => firm.id === top.firmId) : undefined;
  const topFirm: AdminDashboard["topFirm"] = top && topFirmRecord ? {
    name: topFirmRecord.name,
    code: countryCode(topFirmRecord.jurisdiction),
    jurisdiction: topFirmRecord.jurisdiction,
    category: topFirmRecord.membershipCategory,
    score: Math.round(((contributionByFirm.get(top.firmId) ?? 0) / maxContribution) * 100),
    projectsLed: data.lobbyingProjects.filter((project) => project.ownerFirmId === top.firmId).length,
    votesCast: top.votesCast,
    referralsMade: data.cooperationRequests.filter((request) => request.requesterFirmId === top.firmId).length,
  } : null;

  // Expertise exchange funnel + recent referrals.
  const introductions = data.cooperationRequests.length;
  const engaged = data.cooperationRequests.filter((request) => ["accepted", "introduced", "in_progress"].includes(request.status)).length;
  const areaCounts = new Map<string, number>();
  for (const request of data.cooperationRequests) for (const area of request.practiceAreaIds) areaCounts.set(area, (areaCounts.get(area) ?? 0) + 1);
  const topAreaId = [...areaCounts.entries()].sort((a, b) => b[1] - a[1])[0]?.[0];
  const referralStatus = (status: string): { label: string; tone: ReferralRow["tone"] } => {
    if (["accepted", "completed", "introduced"].includes(status)) return { label: status === "introduced" ? "Introduced" : status === "completed" ? "Completed" : "Accepted", tone: "ok" };
    if (["in_progress", "pending", "open"].includes(status)) return { label: "In progress", tone: "warn" };
    if (["declined", "conflict_declared", "withdrawn", "expired", "closed"].includes(status)) return { label: status.charAt(0).toUpperCase() + status.slice(1), tone: "danger" };
    return { label: status, tone: "neutral" };
  };
  const recentReferrals: ReferralRow[] = data.cooperationRequests
    .slice()
    .sort((a, b) => b.createdAt - a.createdAt)
    .slice(0, 4)
    .map((request) => {
      const status = referralStatus(request.status);
      return { id: request.id, fromCode: firmCode(request.requesterFirmId), fromName: firmName(request.requesterFirmId), toCode: firmCode(request.targetFirmId), toName: firmName(request.targetFirmId), area: areaName(request.practiceAreaIds[0]), status: status.label, tone: status.tone, timeLabel: relTime(request.createdAt, now) };
    });

  // Most participating members.
  const memberScores = data.users
    .filter((user) => user.active && user.firmId)
    .map((user) => {
      const votes = new Set(data.votes.filter((vote) => vote.userId === user.id).map((vote) => vote.proposalId)).size;
      const posts = data.discussions.filter((discussion) => discussion.createdByUserId === user.id).length + data.contributions.filter((contribution) => contribution.createdByUserId === user.id).length;
      const referrals = data.cooperationRequests.filter((request) => request.requesterUserId === user.id || request.targetUserId === user.id).length;
      const score = votes * 3 + posts * 2 + referrals * 4;
      return { user, votes, posts, referrals, score };
    })
    .filter((entry) => entry.score > 0)
    .sort((a, b) => b.score - a.score)
    .slice(0, 5);
  const maxScore = Math.max(1, ...memberScores.map((entry) => entry.score));
  const topMembers: TopMember[] = memberScores.map((entry, index) => ({
    rank: index + 1,
    name: entry.user.name,
    initials: initials(entry.user.name),
    firmName: firmName(entry.user.firmId),
    firmCode: firmCode(entry.user.firmId),
    avatarUrl: entry.user.avatarUrl ?? null,
    votes: entry.votes,
    posts: entry.posts,
    referrals: entry.referrals,
    score: entry.score,
    pct: Math.round((entry.score / maxScore) * 100),
  }));

  return {
    kpis,
    participation: { ratePct, activeReps, totalReps, firms: participationFirms },
    news,
    latestVote,
    topFirm,
    expertise: { introductions, engaged, mattersOpened, topArea: topAreaId ? areaName(topAreaId) : "—", recent: recentReferrals },
    topMembers,
  };
}
